import type { PropsWithChildren, ReactNode } from "react";
import { Page, PageBody, PageContent, PageHeader } from "@corpuscomplete/web/components/layout";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { ScopeBreadcrumb } from "@corpuscomplete/web/modules/common/components/scope-breadcrumb";

type RecipeAuthoringPageShellProps = PropsWithChildren<{
  title?: string;
  breadcrumb?: ReactNode;
  isSubmitting?: boolean;
  submitError?: string | null;
  status?: ReactNode;
  headerStatus?: ReactNode;
  headerActions?: ReactNode;
  onCancel?: () => void;
  onSave?: () => void;
}>;

export function RecipeAuthoringPageShell({
  title,
  breadcrumb,
  isSubmitting = false,
  submitError = null,
  status,
  headerStatus,
  headerActions,
  onCancel,
  onSave,
  children,
}: RecipeAuthoringPageShellProps) {
  return (
    <Page>
      <PageBody>
        <PageHeader>
          <div className="mr-auto min-w-0">{breadcrumb ?? <Text>Recipes</Text>}</div>
          {headerStatus}
          {headerActions}
          {onCancel && (
            <Button variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          {onSave && (
            <Button size="sm" onClick={onSave} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          )}
        </PageHeader>
        <PageContent className="bg-muted/20">
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-6">
            {(title || status || submitError) && (
              <div className="flex flex-col gap-2">
                {title ? (
                  <Text variant="h1" className="text-balance">
                    {title}
                  </Text>
                ) : null}
                {status}
                {submitError && (
                  <Text variant="sm" className="text-destructive">
                    {submitError}
                  </Text>
                )}
              </div>
            )}

            <div>{children}</div>
          </div>
        </PageContent>
      </PageBody>
    </Page>
  );
}

export function RecipeAuthoringLoadingPage({ title }: { title: string }) {
  return (
    <RecipeAuthoringPageShell
      title={title}
      breadcrumb={
        <ScopeBreadcrumb scope={null} scopeBehavior="static" crumbs={[{ loading: true }]} />
      }
    >
      <div className="flex items-center justify-center p-8">
        <Text variant="sm" className="text-muted-foreground">
          Loading...
        </Text>
      </div>
    </RecipeAuthoringPageShell>
  );
}
